// hooks/useUserService.js
import { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import userService from '../services/userService';
import { useAuth } from '../context/AuthContext';

const useUserService = () => {
  const { getAccessToken } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiClient = useMemo(() => {
    const client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    client.interceptors.request.use(
      async (config) => {
        try {
          const token = await getAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Error getting access token:', error);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          console.error('Unauthorized access - redirecting to login');
        }
        return Promise.reject(error);
      }
    );

    return client;
  }, [getAccessToken]);

  const service = useMemo(() => userService(apiClient), [apiClient]);

  // Fetch user profile on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const response = await service.getCurrentUser();
        console.log('User profile response:', response);
        // The API returns { success: true, user: {...} }
        setUserProfile(response.user || response);
        setError(null);
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [service]);

  return { userProfile, loading, error, ...service };
};

export default useUserService;